import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const blankQuestion = () => ({ question: '', options: ['', '', '', ''], correct_index: 0, explanation: '' });
const splitList = (s) => s.split(',').map((x) => x.trim()).filter(Boolean);

const QuestionEditor = ({ q, i, onChange, onRemove }) => {
  const set = (patch) => onChange({ ...q, ...patch });
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`edit-question-${i}`}>
      <div className="flex gap-2">
        <Input value={q.question} onChange={(e) => set({ question: e.target.value })} placeholder={`Question ${i + 1}`} data-testid={`edit-question-text-${i}`} />
        <Button variant="ghost" size="icon" onClick={onRemove} aria-label="Remove question" data-testid={`edit-question-remove-${i}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {[0, 1, 2, 3].map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm">
            <input type="radio" name={`correct-${i}`} checked={q.correct_index === o} onChange={() => set({ correct_index: o })} data-testid={`edit-question-${i}-correct-${o}`} />
            <Input value={q.options[o] || ''} placeholder={`Answer ${o + 1}${o > 1 ? ' (optional)' : ''}`}
              onChange={(e) => { const options = [...q.options]; options[o] = e.target.value; set({ options }); }} data-testid={`edit-question-${i}-option-${o}`} />
          </label>
        ))}
      </div>
      <p className="text-[11px] text-slate-400">Tick the circle next to the correct answer.</p>
    </div>
  );
};

export const LessonEditDialog = ({ lesson, open, onOpenChange, onSaved }) => {
  const { api } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!lesson) return;
    setForm({
      title: lesson.title,
      narration: lesson.teach?.narration || '',
      questions: (lesson.questions || []).map((q) => ({ ...q, options: [...q.options, '', '', '', ''].slice(0, 4) })),
      say: (lesson.say_words || []).join(', '),
      trace: (lesson.trace_words || []).join(', '),
    });
  }, [lesson]);

  const save = async () => {
    setSaving(true);
    try {
      const questions = form.questions.map((q) => {
        const kept = q.options.map((o, idx) => ({ o: o.trim(), idx })).filter((x) => x.o);
        const ci = kept.findIndex((x) => x.idx === q.correct_index);
        return { id: q.id, question: q.question.trim(), options: kept.map((x) => x.o), correct_index: ci < 0 ? 99 : ci, explanation: q.explanation || '' };
      });
      if (questions.some((q) => !q.question || q.options.length < 2 || q.correct_index === 99)) {
        toast.error('Each question needs text, at least 2 answers, and a ticked correct answer');
        setSaving(false);
        return;
      }
      const r = await api.put(`/lessons/item/${lesson.id}`, {
        title: form.title, narration: form.narration, questions, say_words: splitList(form.say), trace_words: splitList(form.trace),
      });
      toast.success('Lesson saved');
      onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Could not save');
    } finally { setSaving(false); }
  };

  if (!form) return null;
  const setQ = (i, q) => setForm((f) => ({ ...f, questions: f.questions.map((x, j) => (j === i ? q : x)) }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lesson-edit-dialog">
        <DialogHeader><DialogTitle>Edit lesson</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-lesson-title" /></div>
          <div><Label>Teaching script (read aloud)</Label><Textarea rows={4} value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} data-testid="edit-lesson-narration" /></div>
          <div className="space-y-2">
            <Label>Practice questions</Label>
            {form.questions.map((q, i) => (
              <QuestionEditor key={q.id || i} q={q} i={i} onChange={(nq) => setQ(i, nq)}
                onRemove={() => setForm((f) => ({ ...f, questions: f.questions.filter((_, j) => j !== i) }))} />
            ))}
            <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, questions: [...f.questions, blankQuestion()] }))} data-testid="edit-add-question-btn">
              <Plus className="w-4 h-4 mr-1" /> Add question
            </Button>
          </div>
          <div><Label>Say-it words (comma separated)</Label><Input value={form.say} onChange={(e) => setForm({ ...form, say: e.target.value })} data-testid="edit-lesson-say-words" /></div>
          <div><Label>Trace words (comma separated, max 5 letters each)</Label><Input value={form.trace} onChange={(e) => setForm({ ...form, trace: e.target.value })} data-testid="edit-lesson-trace-words" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="edit-lesson-save-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LessonEditDialog;
